import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '@hotjar/translations';
import { useLocalStorageJSONValue } from '@hotjar/insights-common/hooks/useStorage';
import { BaseIcon, tokens, Tooltip } from '@hotjar/hotsauce';
import { Position } from '@hotjar/navigation/src/events/Position';
import type { EventEmitter } from '@hotjar/navigation/src/types';
import { RoundIconButton } from '../RoundButton';

const Dot = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  width: ${tokens.spacer.x2};
  height: ${tokens.spacer.x2};
  border-radius: 50%;
  background-color: ${tokens.BRAND_COLORS.ember_700};
  border: 1px solid ${tokens.BRAND_COLORS.white};
  color: ${tokens.color.text_default_inverse};
  line-height: 15px;
  font-size: 12px;
  text-align: center;
`;

const Container = styled.div`
  position: relative;
`;

interface GettingStartedItemProps {
  remainingTasks: number;
  toggleSideBar: () => void;
  emitter: EventEmitter;
  isSideBarOpen: boolean;
}

export const GettingStartedItem = ({
  remainingTasks,
  toggleSideBar,
  emitter,
  isSideBarOpen,
}: GettingStartedItemProps) => {
  const { t } = useTranslation('navigation');
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [firstCloseTriggered, setFirstCloseTriggered] =
    useLocalStorageJSONValue<boolean>('gsg_sidebar_first_close');
  const handleOnClick = () => {
    if (!isSideBarOpen) {
      emitter.emit('clickedGSGItem', { position: Position.gsgItem });
      toggleSideBar();
    }
  };

  useEffect(() => {
    if (!isSideBarOpen && !firstCloseTriggered) {
      setIsTooltipVisible(true);

      setTimeout(() => {
        setIsTooltipVisible(false);
        setFirstCloseTriggered(true);
      }, 3000);
    }
  }, [isSideBarOpen, firstCloseTriggered, setFirstCloseTriggered]);

  return (
    <Container>
      <Tooltip visible={isTooltipVisible} text={t('GSGItem.tooltip')}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/no-noninteractive-tabindex */}
        <span css={{ display: 'inline-block' }} tabIndex={0}>
          <RoundIconButton
            icon={<BaseIcon icon="help" pixelSize={20} />}
            description={t('GSGItem.tooltip')}
            tooltipText={t('GSGItem.tooltip')}
            kind="ghost"
            onClick={handleOnClick}
          />
          {remainingTasks ? <Dot>{remainingTasks}</Dot> : null}
        </span>
      </Tooltip>
    </Container>
  );
};
